import { FaroRadio, FaroRadioGroup, TextField } from "@faro-lotv/flat-ui";
import { assert } from "@faro-lotv/foundation";
import { OrthoPhotoGenerationLimitsResponse } from "@faro-lotv/service-wires";
import { Stack } from "@mui/system";
import { debounce } from "es-toolkit";
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  type JSX,
} from "react";
import { useOrthophotoBackend } from "./use-export-orthophoto";
/** The value used for the delay in validating and setting the resolution input value in ms */
const RESOLUTION_INPUT_DEBOUNCE_MS = 100;

export type ViewportSize = {
  /** Width in pixels */
  width: number;
  /** Height in pixels */
  height: number;
};

/** Resolution types available for orthophoto generation */
export const RESOLUTION_TYPES = {
  millimetersPerPixel: {
    label: "Millimeters per Pixel",
    value: "millimetersPerPixel",
    default: 5,
  },
  pixelsPerMeter: {
    label: "Pixels per Meter",
    value: "pixelsPerMeter",
    default: 200,
  },
};

/** Type representing the resolution type selected by the user */
export type ResolutionType =
  (typeof RESOLUTION_TYPES)[keyof typeof RESOLUTION_TYPES]["value"];

/** Width or height in pixels from which most browsers refuse to decode a PNG (2^15) */
const BROWSER_FRIENDLY_SIZE_MAX = 32768;

/**
 * Total pixel count from which most browsers refuse to decode a PNG.
 * Decoded image size must be typically below 2 GB and each pixel has 4 byte (RGBA).
 */
const BROWSER_FRIENDLY_PIXELS_MAX = 500_000_000;

/**
 * @returns true if the generated PNG is likely too large to be opened in a browser
 * @param dimensions the dimensions of the orthophoto to generate
 */
export function isTooLargeForBrowsers({
  width,
  height,
}: ViewportSize): boolean {
  return (
    width >= BROWSER_FRIENDLY_SIZE_MAX ||
    height >= BROWSER_FRIENDLY_SIZE_MAX ||
    width * height > BROWSER_FRIENDLY_PIXELS_MAX
  );
}

/**
 * Validates the resolution based on user provided resolution and ortho camera dimensions on the given plane.
 *
 * @param resolutionType - the resolution type selected by the user
 * @param resolution - resolution value in the units specified by resolutionType
 * @param orthophotoLimits - orthophoto generation limits from the backend
 * @param cameraWidth - optional camera width in meters
 * @param cameraHeight - optional camera height in meters
 * @returns image dimensions if camera dimensions are provided
 * @throws Error with validation message if validation fails
 */
export function validateResolution(
  resolutionType: ResolutionType,
  resolution: number,
  orthophotoLimits: OrthoPhotoGenerationLimitsResponse,
  cameraWidth?: number,
  cameraHeight?: number,
): ViewportSize | undefined {
  // Validate resolution input
  assert(
    !Number.isNaN(resolution) && resolution > 0,
    "Resolution must be a positive number",
  );

  // Convert to meters per pixel for validation
  const metersPerPixel =
    resolutionType === RESOLUTION_TYPES.millimetersPerPixel.value
      ? resolution / 1000
      : 1 / resolution;

  // Validate against backend limits (which are in meters per pixel)
  assert(
    metersPerPixel >= orthophotoLimits.resolutionMin &&
      metersPerPixel <= orthophotoLimits.resolutionMax,
    resolutionType === RESOLUTION_TYPES.millimetersPerPixel.value
      ? `Resolution must be between ${Math.ceil(orthophotoLimits.resolutionMin * 1000)} and ${Math.floor(orthophotoLimits.resolutionMax * 1000)} millimeters per pixel`
      : `Resolution must be between ${Math.ceil(1 / orthophotoLimits.resolutionMax)} and ${Math.floor(1 / orthophotoLimits.resolutionMin)} pixels per meter`,
  );

  // Validate dimensions and pixel count if camera dimensions are provided
  if (cameraWidth && cameraHeight) {
    const pixelsPerMeter = 1 / metersPerPixel;
    const imageWidth = Math.round(pixelsPerMeter * cameraWidth);
    const imageHeight = Math.round(pixelsPerMeter * cameraHeight);
    const totalPixels = imageWidth * imageHeight;
    const imageDimensions = { width: imageWidth, height: imageHeight };

    // Check individual dimensions and total pixel count
    assert(
      imageWidth <= orthophotoLimits.sizeMax &&
        imageHeight <= orthophotoLimits.sizeMax &&
        totalPixels <= orthophotoLimits.pixelsMax,
      "Too large. Reduce resolution or volume.",
    );

    // Additional check for minimum width to accommodate legend (scale bar)
    assert(
      imageWidth >= orthophotoLimits.widthMin,
      "Too small. Increase resolution or volume.",
    );

    return imageDimensions;
  }
}
interface OrthophotoResolutionInputProps {
  /** The resolution type selected by the user */
  resolutionType: ResolutionType;

  /** Setter for the resolution type */
  setResolutionType: Dispatch<SetStateAction<string>>;

  /** The user defined resolution for the orthophoto generation */
  resolution: number;

  /** Setter for the resolution */
  setResolution: Dispatch<SetStateAction<number>>;

  /** Error text for the resolution input */
  resolutionErrorText?: string;

  /** Setter for the resolution error text */
  setResolutionErrorText: Dispatch<SetStateAction<string | undefined>>;
}

/**
 * @returns a resolution input component with options for meters per pixel and pixels per meter
 */
export function OrthophotoResolutionInput({
  resolutionType,
  setResolutionType,
  resolution,
  setResolution,
  resolutionErrorText,
  setResolutionErrorText,
}: OrthophotoResolutionInputProps): JSX.Element {
  const { orthophotoLimits } = useOrthophotoBackend();

  const handleResolutionInputChange = useCallback(
    (newResolution: string) => {
      debounce(() => {
        const parsed = parseFloat(newResolution);
        setResolution(parsed);

        // Validate resolution input
        if (Number.isNaN(parsed) || parsed <= 0) {
          setResolutionErrorText("Resolution must be a positive number");
          return;
        }

        try {
          validateResolution(resolutionType, parsed, orthophotoLimits);
          setResolutionErrorText(undefined);
        } catch (error) {
          setResolutionErrorText(
            error instanceof Error ? error.message : "Invalid resolution",
          );
        }
      }, RESOLUTION_INPUT_DEBOUNCE_MS)();
    },
    [orthophotoLimits, resolutionType, setResolution, setResolutionErrorText],
  );

  const switchResolutionTypeWithConversion = useCallback(
    (_: ChangeEvent<HTMLInputElement>, newType: ResolutionType) => {
      setResolutionType(newType);

      // Check if current resolution is valid for conversion
      if (Number.isNaN(resolution) || resolution <= 0) {
        // Use default value for the new type if current value is invalid
        const defaultResolution =
          newType === RESOLUTION_TYPES.millimetersPerPixel.value
            ? RESOLUTION_TYPES.millimetersPerPixel.default
            : RESOLUTION_TYPES.pixelsPerMeter.default;

        setResolution(defaultResolution);
        setResolutionErrorText(undefined);
        return;
      }

      // Convert between units
      const convertedResolution = parseFloat((1000 / resolution).toFixed(4));
      setResolution(convertedResolution);

      // Validate the converted resolution against the new type
      try {
        validateResolution(newType, convertedResolution, orthophotoLimits);
        setResolutionErrorText(undefined);
      } catch (error) {
        setResolutionErrorText(
          error instanceof Error ? error.message : "Invalid resolution",
        );
      }
    },
    [
      orthophotoLimits,
      resolution,
      setResolution,
      setResolutionErrorText,
      setResolutionType,
    ],
  );

  return (
    <Stack>
      <FaroRadioGroup
        onChange={switchResolutionTypeWithConversion}
        label="Resolution"
      >
        <FaroRadio
          label={RESOLUTION_TYPES.millimetersPerPixel.label}
          value={RESOLUTION_TYPES.millimetersPerPixel.value}
          checked={
            resolutionType === RESOLUTION_TYPES.millimetersPerPixel.value
          }
        />
        <FaroRadio
          label={RESOLUTION_TYPES.pixelsPerMeter.label}
          value={RESOLUTION_TYPES.pixelsPerMeter.value}
          checked={resolutionType === RESOLUTION_TYPES.pixelsPerMeter.value}
        />
      </FaroRadioGroup>
      <TextField
        type="number"
        fullWidth
        placeholder="Enter resolution"
        text={resolution.toString()}
        onTextChanged={handleResolutionInputChange}
        error={resolutionErrorText}
        sx={{ mt: 1 }}
      />
    </Stack>
  );
}
